import React, { useMemo } from 'react';
import { specifyStatus } from '../../../../../utilities/generalMappings';
import { Status } from '../../../../../types/redux/data/dataTypes';
import { isNavReconPipelineStageSucceeded } from '../pipelineMetadata';
import StatsPill from '../../../../ui/StatsPill';

interface NavReconOverviewStatsProps {
  title: string;
  rows: any[];
  showKpis?: boolean;
}

const NavReconOverviewStats: React.FC<NavReconOverviewStatsProps> = ({
  title,
  rows,
  showKpis = true,
}) => {
  const totalFunds = rows.length;

  // Number of funds where "NAV Reconciliation Calculation" latest snapshot succeeded
  const completeFundsCount = useMemo(() => {
    return rows.filter((r) => {
      const stage = r.pipelineFundMetadata?.stages?.find(
        (s: any) => s.rs_data_name === 'nav_reconciliation_risk_monitor'
      );
      return isNavReconPipelineStageSucceeded(stage?.snapshots);
    }).length;
  }, [rows]);

  // Helper to determine if a cell status is Alert or Fail
  const isFailOrAlert = (val: string | null | undefined) => {
    if (!val) return false;
    const status = specifyStatus(val);
    return status === Status.Fail || status === Status.Alert;
  };

  // Number of funds requiring action (has either a fail or alert in any status column, and is not manually approved)
  const actionRequiredCount = useMemo(() => {
    return rows.filter((r) => {
      if (!r.overviewHasPayload) return false;
      if (r.auditStatus === 'approved') return false;

      const statusFields = [
        r.share_class_price_deviation_status,
        r.est_return_status,
        r.unit_return_status,
        r.alertStatus_six_caceis_returns_status,
        r.alertStatus_caceis_share_price_returns_status,
        r.alertStatus_six_share_price_returns_status,
        r.alertStatus_sc_nav_px_returns_status,
        r.alertStatus_sc_unit_status,
      ];

      return statusFields.some(isFailOrAlert);
    }).length;
  }, [rows]);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 w-full">
      {/* Table Title */}
      <h2 className="text-base font-semibold text-mainblue whitespace-nowrap">
        {title}
      </h2>

      {showKpis ? (
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <StatsPill
            pulse
            label="NAV Calculation Complete"
            value={completeFundsCount}
            total={totalFunds}
          />
          <StatsPill
            status={actionRequiredCount > 0 ? 'fail' : 'pass'}
            label="Requires Audit"
            value={actionRequiredCount}
            total={completeFundsCount}
          />
        </div>
      ) : null}
    </div>
  );
};

export default NavReconOverviewStats;
